import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { Badge, Button, Card, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { NumberInput, Select, Switch, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import type { Indexed } from '../../../lib/types';
import { ColourPicker } from '../../bookings/components/ColourPicker';
import { SWATCHES } from '../../bookings/lib/data';
import { PriorityTag } from '../components/TicketBits';
import {
  TICKETS_KEY, byStatusOrder, categoryLabel, formatMinutes, ticketErrorText, useLookups, useTicketPriorities, useTicketStatuses
} from '../lib/data';

const MUTED = 'var(--rr-color-text-muted)';

function useRefresh() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: [TICKETS_KEY] });
}

/** Delete after a confirm that names the thing; a status or priority still in use says so. */
function confirmDelete(t: ReturnType<typeof useT>, name: string, run: () => Promise<void>, onDone: () => void, title: string) {
  openConfirmModal({
    title: t('tickets.settings.delete.title', { name }),
    children: <Text role="bodySmall">{t('tickets.settings.delete.body', { name })}</Text>,
    labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
    confirmProps: { color: 'red' },
    onConfirm: async () => {
      try {
        await run();
        notifySuccess(t('tickets.settings.toast.deleted', { name }), title);
        onDone();
      } catch (e) {
        notifyError(ticketErrorText(t, e), title);
      }
    }
  });
}

function ErrorRetry({ error, retry }: { error: unknown; retry: () => void }) {
  const t = useT();
  return (
    <Alert color="error" role="alert">
      <Group gap="sm" justify="space-between" wrap="wrap">
        <span>{ticketErrorText(t, error)}</span>
        <Button size="xs" variant="default" onClick={retry}>{t('action.retry')}</Button>
      </Group>
    </Alert>
  );
}

// ── Statuses ──────────────────────────────────────────────────────────────

export function StatusesSection({ canEdit }: { canEdit: boolean }) {
  const t = useT();
  const refresh = useRefresh();
  const q = useTicketStatuses();
  const lk = useLookups();
  const [editing, setEditing] = useState<tickets.Status | { category: tickets.StatusCategory } | null>(null);
  const all = [...(q.data?.data ?? [])].sort(byStatusOrder);

  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Title order={4}>{t('tickets.statuses.title')}</Title>
        <Text role="bodySmall" c={MUTED}>{t('tickets.statuses.intro')}</Text>
      </Stack>
      {q.error && <ErrorRetry error={q.error} retry={() => void q.refetch()} />}
      {tickets.STATUS_CATEGORIES.map((category) => {
        const rows = all.filter((s) => s.category === category) as Indexed<tickets.Status>[];
        return (
          <Card withBorder key={category}>
            <Stack gap="sm">
              <Group gap="sm" justify="space-between">
                <Stack gap={0}>
                  <Text role="subtitle" fw={600}>{categoryLabel(t, category)}</Text>
                  <Text role="caption" c={MUTED}>{t(`tickets.statuses.categoryHint.${category}`)}</Text>
                </Stack>
                {canEdit && <Button size="xs" variant="subtle" onClick={() => setEditing({ category })}>{t('tickets.statuses.add')}</Button>}
              </Group>
              {q.isPending ? null : rows.length === 0 ? (
                <Text role="bodySmall" c={MUTED}>{t('tickets.statuses.none')}</Text>
              ) : (
                <DataTable<Indexed<tickets.Status>>
                  records={rows}
                  idAccessor="id"
                  onRowClick={(r) => setEditing(r)}
                  columns={[
                    {
                      accessor: 'name',
                      title: t('common.name'),
                      render: (r) => (
                        <Group gap="xs">
                          <Text role="bodySmall" fw={500}>{r.name}</Text>
                          {r.is_default && <Badge size="xs" variant="light">{t('tickets.default')}</Badge>}
                        </Group>
                      )
                    },
                    { accessor: 'client_label', title: t('tickets.statuses.clientLabel'), render: (r) => <Text role="bodySmall" c={r.client_label ? undefined : MUTED}>{r.client_label || t('common.none')}</Text> },
                    { accessor: 'type_id', title: t('tickets.statuses.forType'), render: (r) => <Text role="bodySmall">{r.type_id ? lk.typeById.get(r.type_id)?.name ?? t('common.unknown') : t('tickets.statuses.everyType')}</Text> },
                    {
                      accessor: 'actions',
                      title: t('common.actions'),
                      render: (r) => canEdit ? (
                        <span onClick={(e) => e.stopPropagation()}>
                          <Button size="xs" variant="subtle" color="red" aria-label={t('tickets.settings.deleteNamed', { name: r.name })}
                            onClick={() => confirmDelete(t, r.name, () => tickets.deleteStatus(r.id), refresh, t('tickets.statuses.title'))}>
                            {t('action.delete')}
                          </Button>
                        </span>
                      ) : null
                    }
                  ]}
                />
              )}
            </Stack>
          </Card>
        );
      })}
      <StatusModal target={editing} canEdit={canEdit} types={lk.types} onClose={() => setEditing(null)} onSaved={refresh} />
    </Stack>
  );
}

function StatusModal({ target, canEdit, types, onClose, onSaved }: {
  target: tickets.Status | { category: tickets.StatusCategory } | null;
  canEdit: boolean;
  types: tickets.TicketType[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const existing = target && 'id' in target ? target : null;
  const [d, setD] = useState({ name: '', category: 'open' as tickets.StatusCategory, client_label: '', type_id: '', sort_order: 0 as number | '', is_default: false });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    setD({
      name: existing?.name ?? '',
      category: target.category,
      client_label: existing?.client_label ?? '',
      type_id: existing?.type_id ?? '',
      sort_order: existing?.sort_order ?? 0,
      is_default: existing?.is_default ?? false
    });
    setTried(false);
    setError(null);
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    setTried(true);
    if (!d.name.trim()) return;
    setBusy(true);
    setError(null);
    const body = {
      name: d.name.trim(),
      category: d.category,
      client_label: d.client_label.trim() || null,
      type_id: d.type_id || null,
      sort_order: d.sort_order === '' ? 0 : d.sort_order,
      is_default: d.is_default
    };
    try {
      if (existing) await tickets.updateStatus(existing.id, body); else await tickets.createStatus(body);
      notifySuccess(t('tickets.settings.toast.savedNamed', { name: body.name }), t('tickets.statuses.title'));
      onSaved();
      onClose();
    } catch (e) {
      setError(ticketErrorText(t, e));
    } finally {
      setBusy(false);
    }
  }

  const ro = !canEdit;
  return (
    <Modal opened={!!target} onClose={onClose} title={existing ? existing.name : t('tickets.statuses.add')} size="md">
      <form onSubmit={(e) => { e.preventDefault(); if (canEdit) void save(); }}>
        <Stack gap="md">
          <TextInput label={t('common.name')} value={d.name} onChange={(e) => setD({ ...d, name: e.currentTarget.value })} required maxLength={60} disabled={ro}
            error={tried && !d.name.trim() ? t('tickets.settings.error.name') : undefined} data-autofocus />
          <Select label={t('tickets.statuses.category')} value={d.category} onChange={(e) => setD({ ...d, category: e.currentTarget.value as tickets.StatusCategory })} disabled={ro}
            data={tickets.STATUS_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(t, c) }))}
            description={t(`tickets.statuses.categoryHint.${d.category}`)} />
          <TextInput label={t('tickets.statuses.clientLabel')} value={d.client_label} onChange={(e) => setD({ ...d, client_label: e.currentTarget.value })} maxLength={120} disabled={ro}
            description={t('tickets.statuses.clientLabelHint')} />
          <Select label={t('tickets.statuses.forType')} value={d.type_id} onChange={(e) => setD({ ...d, type_id: e.currentTarget.value })} disabled={ro}
            data={[{ value: '', label: t('tickets.statuses.everyType') }, ...types.map((x) => ({ value: x.id, label: x.name }))]} />
          <Group gap="sm" grow align="flex-start" wrap="wrap">
            <NumberInput label={t('tickets.order')} value={d.sort_order} onChange={(v) => setD({ ...d, sort_order: v })} min={0} max={1000} disabled={ro}
              description={t('tickets.statuses.orderHint')} />
            <Switch label={t('tickets.statuses.isDefault')} description={t('tickets.statuses.isDefaultHint')} checked={d.is_default} onChange={(on) => setD({ ...d, is_default: on })} disabled={ro} />
          </Group>
          {error && <Alert color="error" role="alert">{error}</Alert>}
          <Group gap="sm" justify="flex-end">
            <Button variant="default" onClick={onClose}>{canEdit ? t('action.cancel') : t('action.close')}</Button>
            {canEdit && <Button type="submit" loading={busy}>{t('action.save')}</Button>}
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

// ── Priorities ────────────────────────────────────────────────────────────

export function PrioritiesSection({ canEdit }: { canEdit: boolean }) {
  const t = useT();
  const refresh = useRefresh();
  const q = useTicketPriorities();
  const [editing, setEditing] = useState<tickets.Priority | 'new' | null>(null);
  const rows = [...(q.data?.data ?? [])].sort((a, b) => b.rank - a.rank) as Indexed<tickets.Priority>[];

  return (
    <Stack gap="md">
      <TableShell
        title={t('tickets.priorities.title')}
        description={t('tickets.priorities.intro')}
        actions={canEdit ? <Button size="sm" onClick={() => setEditing('new')}>{t('tickets.priorities.add')}</Button> : undefined}
        loading={q.isPending}
        error={q.error ? <ErrorRetry error={q.error} retry={() => void q.refetch()} /> : undefined}
        isEmpty={!q.isPending && rows.length === 0}
        empty={<EmptyState title={t('tickets.priorities.empty')} description={t('tickets.priorities.emptyHint')} />}
      >
        <DataTable<Indexed<tickets.Priority>>
          records={rows}
          idAccessor="id"
          onRowClick={(r) => setEditing(r)}
          columns={[
            {
              accessor: 'name',
              title: t('common.name'),
              render: (r) => (
                <Group gap="xs">
                  <PriorityTag priority={r} />
                  {r.is_default && <Badge size="xs" variant="light">{t('tickets.default')}</Badge>}
                </Group>
              )
            },
            { accessor: 'rank', title: t('tickets.priorities.rank'), textAlign: 'right', render: (r) => r.rank },
            {
              accessor: 'actions',
              title: t('common.actions'),
              render: (r) => canEdit ? (
                <span onClick={(e) => e.stopPropagation()}>
                  <Button size="xs" variant="subtle" color="red" aria-label={t('tickets.settings.deleteNamed', { name: r.name })}
                    onClick={() => confirmDelete(t, r.name, () => tickets.deletePriority(r.id), refresh, t('tickets.priorities.title'))}>
                    {t('action.delete')}
                  </Button>
                </span>
              ) : null
            }
          ]}
        />
      </TableShell>
      <PriorityModal target={editing} canEdit={canEdit} nextRank={(rows[0]?.rank ?? 0) + 1} onClose={() => setEditing(null)} onSaved={refresh} />
    </Stack>
  );
}

function PriorityModal({ target, canEdit, nextRank, onClose, onSaved }: {
  target: tickets.Priority | 'new' | null;
  canEdit: boolean;
  nextRank: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const existing = target && target !== 'new' ? target : null;
  const [d, setD] = useState({ name: '', colour: SWATCHES[0] as string | null, rank: 1 as number | '', is_default: false });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    setD({ name: existing?.name ?? '', colour: existing?.colour ?? SWATCHES[0], rank: existing?.rank ?? nextRank, is_default: existing?.is_default ?? false });
    setTried(false);
    setError(null);
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    setTried(true);
    if (!d.name.trim()) return;
    setBusy(true);
    setError(null);
    const body = { name: d.name.trim(), colour: d.colour, rank: d.rank === '' ? 1 : d.rank, is_default: d.is_default };
    try {
      if (existing) await tickets.updatePriority(existing.id, body); else await tickets.createPriority(body);
      notifySuccess(t('tickets.settings.toast.savedNamed', { name: body.name }), t('tickets.priorities.title'));
      onSaved();
      onClose();
    } catch (e) {
      setError(ticketErrorText(t, e));
    } finally {
      setBusy(false);
    }
  }

  const ro = !canEdit;
  return (
    <Modal opened={!!target} onClose={onClose} title={existing ? existing.name : t('tickets.priorities.add')} size="md">
      <form onSubmit={(e) => { e.preventDefault(); if (canEdit) void save(); }}>
        <Stack gap="md">
          <TextInput label={t('common.name')} value={d.name} onChange={(e) => setD({ ...d, name: e.currentTarget.value })} required maxLength={40} disabled={ro}
            error={tried && !d.name.trim() ? t('tickets.settings.error.name') : undefined} data-autofocus />
          <ColourPicker label={t('tickets.priorities.colour')} value={d.colour} onChange={(c) => setD({ ...d, colour: c })} disabled={ro} />
          <NumberInput label={t('tickets.priorities.rank')} value={d.rank} onChange={(v) => setD({ ...d, rank: v })} min={1} max={100} disabled={ro}
            description={t('tickets.priorities.rankHint')} />
          <Switch label={t('tickets.priorities.isDefault')} description={t('tickets.priorities.isDefaultHint')} checked={d.is_default} onChange={(on) => setD({ ...d, is_default: on })} disabled={ro} />
          {error && <Alert color="error" role="alert">{error}</Alert>}
          <Group gap="sm" justify="flex-end">
            <Button variant="default" onClick={onClose}>{canEdit ? t('action.cancel') : t('action.close')}</Button>
            {canEdit && <Button type="submit" loading={busy}>{t('action.save')}</Button>}
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

// ── Response targets (SLA policies) ───────────────────────────────────────

type Unit = 'minutes' | 'hours';

/** Minutes as typed: a value and a unit, the unit chosen so the value is whole when it can be. */
function splitUnit(minutes: number | null): { value: number | ''; unit: Unit } {
  if (minutes == null) return { value: '', unit: 'hours' };
  return minutes % 60 === 0 ? { value: minutes / 60, unit: 'hours' } : { value: minutes, unit: 'minutes' };
}
function joinUnit(value: number | '', unit: Unit): number | null {
  if (value === '' || value <= 0) return null;
  return Math.round(unit === 'hours' ? value * 60 : value);
}

export function SlaSection({ canEdit }: { canEdit: boolean }) {
  const t = useT();
  const refresh = useRefresh();
  const lk = useLookups();
  const q = useQuery({ queryKey: [TICKETS_KEY, 'sla-policies'], queryFn: () => tickets.listSlaPolicies() });
  const [editing, setEditing] = useState<tickets.SlaPolicy | 'new' | null>(null);
  const rows = [...(q.data?.data ?? [])].sort((a, b) => a.sort_order - b.sort_order) as Indexed<tickets.SlaPolicy>[];

  return (
    <Stack gap="md">
      <TableShell
        title={t('tickets.sla.policies')}
        description={t('tickets.sla.policiesIntro')}
        actions={canEdit ? <Button size="sm" data-guide="tickets-sla-add" onClick={() => setEditing('new')}>{t('tickets.sla.add')}</Button> : undefined}
        loading={q.isPending}
        error={q.error ? <ErrorRetry error={q.error} retry={() => void q.refetch()} /> : undefined}
        isEmpty={!q.isPending && rows.length === 0}
        empty={<EmptyState title={t('tickets.sla.empty')} description={t('tickets.sla.emptyHint')} />}
      >
        <DataTable<Indexed<tickets.SlaPolicy>>
          records={rows}
          idAccessor="id"
          onRowClick={(r) => setEditing(r)}
          columns={[
            { accessor: 'name', title: t('common.name'), render: (r) => <Text role="bodySmall" fw={500}>{r.name}</Text> },
            { accessor: 'type_id', title: t('tickets.field.type'), render: (r) => <Text role="bodySmall">{r.type_id ? lk.typeById.get(r.type_id)?.name ?? t('common.unknown') : t('tickets.sla.anyType')}</Text> },
            { accessor: 'priority_id', title: t('tickets.field.priority'), render: (r) => <Text role="bodySmall">{r.priority_id ? lk.priorityById.get(r.priority_id)?.name ?? t('common.unknown') : t('tickets.sla.anyPriority')}</Text> },
            { accessor: 'first_response_minutes', title: t('tickets.sla.firstResponse'), render: (r) => formatMinutes(t, r.first_response_minutes) },
            { accessor: 'resolution_minutes', title: t('tickets.sla.resolution'), render: (r) => formatMinutes(t, r.resolution_minutes) },
            { accessor: 'business_hours_only', title: t('tickets.sla.clock'), render: (r) => <Text role="caption">{r.business_hours_only ? t('tickets.sla.businessHours') : t('tickets.sla.allHours')}</Text> },
            { accessor: 'active', title: t('common.status'), render: (r) => <Badge size="sm" variant="light" color={r.active ? 'green' : 'gray'}>{r.active ? t('tickets.active') : t('tickets.inactive')}</Badge> }
          ]}
        />
      </TableShell>
      <Text role="caption" c={MUTED}>{t('tickets.sla.orderHint')}</Text>
      <SlaModal target={editing} canEdit={canEdit} nextOrder={(rows[rows.length - 1]?.sort_order ?? 0) + 1} onClose={() => setEditing(null)} onSaved={refresh} />
    </Stack>
  );
}

function SlaModal({ target, canEdit, nextOrder, onClose, onSaved }: {
  target: tickets.SlaPolicy | 'new' | null;
  canEdit: boolean;
  nextOrder: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const lk = useLookups();
  const existing = target && target !== 'new' ? target : null;
  const blank = { name: '', type_id: '', priority_id: '', fr: splitUnit(60), res: splitUnit(24 * 60), business_hours_only: true, active: true, sort_order: 0 as number | '' };
  const [d, setD] = useState(blank);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    setD(existing ? {
      name: existing.name,
      type_id: existing.type_id ?? '',
      priority_id: existing.priority_id ?? '',
      fr: splitUnit(existing.first_response_minutes),
      res: splitUnit(existing.resolution_minutes),
      business_hours_only: existing.business_hours_only,
      active: existing.active,
      sort_order: existing.sort_order
    } : { ...blank, sort_order: nextOrder });
    setTried(false);
    setError(null);
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const fr = joinUnit(d.fr.value, d.fr.unit);
  const res = joinUnit(d.res.value, d.res.unit);
  const problem = !d.name.trim() ? 'name' : fr == null && res == null ? 'times' : null;

  async function save() {
    setTried(true);
    if (problem) return;
    setBusy(true);
    setError(null);
    const body = {
      name: d.name.trim(),
      type_id: d.type_id || null,
      priority_id: d.priority_id || null,
      first_response_minutes: fr,
      resolution_minutes: res,
      business_hours_only: d.business_hours_only,
      active: d.active,
      sort_order: d.sort_order === '' ? 0 : d.sort_order
    };
    try {
      if (existing) await tickets.updateSlaPolicy(existing.id, body); else await tickets.createSlaPolicy(body);
      notifySuccess(t('tickets.settings.toast.savedNamed', { name: body.name }), t('tickets.sla.policies'));
      onSaved();
      onClose();
    } catch (e) {
      setError(ticketErrorText(t, e));
    } finally {
      setBusy(false);
    }
  }

  function remove() {
    if (!existing) return;
    confirmDelete(t, existing.name, () => tickets.deleteSlaPolicy(existing.id), () => { onSaved(); onClose(); }, t('tickets.sla.policies'));
  }

  const ro = !canEdit;
  const unitData = [{ value: 'minutes', label: t('tickets.unit.minutes') }, { value: 'hours', label: t('tickets.unit.hours') }];
  return (
    <Modal opened={!!target} onClose={onClose} title={existing ? existing.name : t('tickets.sla.add')} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); if (canEdit) void save(); }}>
        <Stack gap="md">
          <TextInput label={t('common.name')} value={d.name} onChange={(e) => setD({ ...d, name: e.currentTarget.value })} required maxLength={80} disabled={ro}
            error={tried && problem === 'name' ? t('tickets.settings.error.name') : undefined} data-autofocus />
          <Group gap="sm" grow wrap="wrap" align="flex-start" data-guide="sla-match">
            <Select label={t('tickets.field.type')} value={d.type_id} onChange={(e) => setD({ ...d, type_id: e.currentTarget.value })} disabled={ro}
              data={[{ value: '', label: t('tickets.sla.anyType') }, ...lk.types.map((x) => ({ value: x.id, label: x.name }))]} />
            <Select label={t('tickets.field.priority')} value={d.priority_id} onChange={(e) => setD({ ...d, priority_id: e.currentTarget.value })} disabled={ro}
              data={[{ value: '', label: t('tickets.sla.anyPriority') }, ...lk.priorities.map((x) => ({ value: x.id, label: x.name }))]} />
          </Group>
          <Group gap="sm" wrap="wrap" align="flex-start" data-guide="sla-first">
            <NumberInput label={t('tickets.sla.firstResponse')} value={d.fr.value} onChange={(v) => setD({ ...d, fr: { ...d.fr, value: v } })} min={0} max={100000} disabled={ro}
              description={t('tickets.sla.firstResponseHint')} />
            <Select label={t('tickets.unit.label')} value={d.fr.unit} onChange={(e) => setD({ ...d, fr: { ...d.fr, unit: e.currentTarget.value as Unit } })} disabled={ro} data={unitData} />
          </Group>
          <Group gap="sm" wrap="wrap" align="flex-start" data-guide="sla-resolution">
            <NumberInput label={t('tickets.sla.resolution')} value={d.res.value} onChange={(v) => setD({ ...d, res: { ...d.res, value: v } })} min={0} max={100000} disabled={ro}
              description={t('tickets.sla.resolutionHint')} />
            <Select label={t('tickets.unit.label')} value={d.res.unit} onChange={(e) => setD({ ...d, res: { ...d.res, unit: e.currentTarget.value as Unit } })} disabled={ro} data={unitData} />
          </Group>
          {tried && problem === 'times' && <Text role="caption" c="var(--rr-color-error)">{t('tickets.sla.error.times')}</Text>}
          <div data-guide="sla-hours">
            <Switch label={t('tickets.sla.businessHoursOnly')} description={t('tickets.sla.businessHoursOnlyHint')} checked={d.business_hours_only}
              onChange={(on) => setD({ ...d, business_hours_only: on })} disabled={ro} />
          </div>
          <Group gap="sm" grow align="flex-start" wrap="wrap">
            <NumberInput label={t('tickets.order')} value={d.sort_order} onChange={(v) => setD({ ...d, sort_order: v })} min={0} max={1000} disabled={ro}
              description={t('tickets.sla.orderHint')} />
            <Switch label={t('tickets.active')} checked={d.active} onChange={(on) => setD({ ...d, active: on })} disabled={ro} />
          </Group>
          {error && <Alert color="error" role="alert">{error}</Alert>}
          <Group gap="sm" justify="space-between">
            <div>{canEdit && existing && <Button variant="subtle" color="red" onClick={remove}>{t('action.delete')}</Button>}</div>
            <Group gap="sm">
              <Button variant="default" onClick={onClose}>{canEdit ? t('action.cancel') : t('action.close')}</Button>
              {canEdit && <Button type="submit" loading={busy} data-guide="sla-save">{t('action.save')}</Button>}
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
